/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { HTMLAttributes, useEffect, useState } from 'preact/compat';
import { Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import {
  Price,
  Button,
  IllustratedMessage,
  Picker,
  Pagination,
  type PickerOption,
} from '@adobe-commerce/elsie/components';
import { events } from '@adobe-commerce/event-bus';
import {
  QuotesListTable as QuotesListTableComponent,
  QuoteRowData,
} from '@/quote-management/components';
import { negotiableQuotes } from '@/quote-management/api';
import { getDefaultPageSizeOptions } from '@/quote-management/data/transforms';
import {
  NegotiableQuotesListModel,
  NegotiableQuoteListEntry,
} from '@/quote-management/data/models';
import { formattedDate } from '@/quote-management/utils/dateUtils';

export interface QuotesListTableProps extends HTMLAttributes<HTMLDivElement> {
  pageSize?: number;
  showItemRange?: boolean;
  showPageSizePicker?: boolean;
  showPagination?: boolean;
  onViewQuote?: (quoteId: string, quoteName: string, status: string) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onPageChange?: (page: number) => void;
  slots?: {
    /** Slot for customizing the quote name cell content */
    QuoteName?: SlotProps<{ quote: NegotiableQuoteListEntry }>;
    /** Slot for customizing the created date cell content */
    Created?: SlotProps<{ quote: NegotiableQuoteListEntry }>;
    /** Slot for customizing the created by cell content */
    CreatedBy?: SlotProps<{ quote: NegotiableQuoteListEntry }>;
    /** Slot for customizing the status cell content */
    Status?: SlotProps<{ quote: NegotiableQuoteListEntry }>;
    /** Slot for customizing the last updated cell content */
    LastUpdated?: SlotProps<{ quote: NegotiableQuoteListEntry }>;
    /** Slot for customizing the quote template cell content */
    QuoteTemplate?: SlotProps<{ quote: NegotiableQuoteListEntry }>;
    /** Slot for customizing the quote total cell content */
    QuoteTotal?: SlotProps<{ quote: NegotiableQuoteListEntry }>;
    /** Slot for customizing the actions cell content */
    Actions?: SlotProps<{
      quote: NegotiableQuoteListEntry;
      onViewQuote?: (id: string, name: string, status: string) => void;
    }>;
    /** Slot for customizing the empty quotes message */
    EmptyQuotes?: SlotProps;
    /** Slot for customizing the item range display */
    ItemRange?: SlotProps<{
      startItem: number;
      endItem: number;
      totalCount: number;
      currentPage: number;
      pageSize: number;
    }>;
    /** Slot for customizing the page size picker */
    PageSizePicker?: SlotProps<{
      pageSize: number;
      pageSizeOptions: number[];
      onPageSizeChange?: (pageSize: number) => void;
    }>;
    /** Slot for customizing the pagination */
    Pagination?: SlotProps<{
      currentPage: number;
      totalPages: number;
      onChange?: (page: number) => void;
    }>;
  };
}

export const QuotesListTable: Container<QuotesListTableProps> = ({
  pageSize,
  showItemRange = true,
  showPageSizePicker = true,
  showPagination = true,
  onViewQuote,
  onPageSizeChange,
  onPageChange,
  slots,
  ...props
}) => {
  const [quotesData, setQuotesData] =
    useState<NegotiableQuotesListModel | null>(null);
  const [loading, setLoading] = useState(true);

  // Default to first page size option if no pageSize prop provided
  const defaultPageSize = getDefaultPageSizeOptions()[0];
  const [currentPageSize, setCurrentPageSize] = useState(
    pageSize || defaultPageSize
  );
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch quotes data when authenticated
  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        setLoading(true);
        const data = await negotiableQuotes({
          pageSize: currentPageSize,
          currentPage,
        });
        setQuotesData(data);
      } catch (error) {
        console.error('Failed to fetch quotes:', error);
      } finally {
        setLoading(false);
      }
    };

    // Listen for authentication events
    const unsubscribe = events.on(
      'authenticated',
      (isAuthenticated) => {
        if (isAuthenticated) {
          fetchQuotes();
        } else {
          setQuotesData(null);
          setLoading(false);
        }
      },
      { eager: true }
    ); // eager: true means it fires immediately with current state

    return () => {
      unsubscribe?.off();
    };
  }, [currentPageSize, currentPage]);

  // Listen for quote renamed event to refresh the list
  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const data = await negotiableQuotes({
          pageSize: currentPageSize,
          currentPage,
        });
        setQuotesData(data);
      } catch (error) {
        console.error('Failed to fetch quotes:', error);
      }
    };

    const unsubscribe = events.on(
      'quote-management/quote-renamed',
      () => {
        // Refetch quotes list to show the updated name
        fetchQuotes();
      },
      { eager: true }
    );

    return () => {
      unsubscribe?.off();
    };
  }, [currentPageSize, currentPage]);

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setCurrentPageSize(newPageSize);
    setCurrentPage(1); // Reset to page 1 when page size changes
    onPageSizeChange?.(newPageSize); // Also call parent callback
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    onPageChange?.(newPage); // Also call parent callback
  };

  // Handle page size picker selection
  const handlePageSizeSelect = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    const value = target?.value;
    if (value) {
      handlePageSizeChange(Number(value));
    }
  };

  // Prepare transformed quote data for table component
  const prepareRowData = (
    quotes: NegotiableQuoteListEntry[],
    slots?: QuotesListTableProps['slots'],
    onViewQuote?: (quoteId: string, quoteName: string, status: string) => void
  ): QuoteRowData[] => {
    return quotes
      .filter((quote) => quote?.uid) // Filter out null quotes
      .map((quote) => {
        const createdByName = `${quote.buyer.firstname} ${quote.buyer.lastname}`;

        return {
          id: quote.uid,
          quoteName: (
            <Slot name="QuoteName" slot={slots?.QuoteName} context={{ quote }}>
              <span>{quote.name}</span>
            </Slot>
          ),
          created: (
            <Slot name="Created" slot={slots?.Created} context={{ quote }}>
              <span>
                {quote.createdAt
                  ? formattedDate(quote.createdAt, 'short')
                  : 'N/A'}
              </span>
            </Slot>
          ),
          createdBy: (
            <Slot name="CreatedBy" slot={slots?.CreatedBy} context={{ quote }}>
              <span>{createdByName}</span>
            </Slot>
          ),
          status: (
            <Slot name="Status" slot={slots?.Status} context={{ quote }}>
              <span>{quote.status}</span>
            </Slot>
          ),
          lastUpdated: (
            <Slot
              name="LastUpdated"
              slot={slots?.LastUpdated}
              context={{ quote }}
            >
              <span>
                {quote.updatedAt
                  ? formattedDate(quote.updatedAt, 'short')
                  : 'N/A'}
              </span>
            </Slot>
          ),
          quoteTemplate: (
            <Slot
              name="QuoteTemplate"
              slot={slots?.QuoteTemplate}
              context={{ quote }}
            >
              <span>{quote.templateName}</span>
            </Slot>
          ),
          quoteTotal: (
            <Slot
              name="QuoteTotal"
              slot={slots?.QuoteTotal}
              context={{ quote }}
            >
              <Price
                amount={quote.prices?.grandTotal?.value}
                currency={quote.prices?.grandTotal?.currency}
              />
            </Slot>
          ),
          actions: (
            <Slot
              name="Actions"
              slot={slots?.Actions}
              context={{ quote, onViewQuote }}
            >
              <Button
                variant="tertiary"
                size="medium"
                onClick={() =>
                  onViewQuote?.(quote.uid, quote.name, quote.status)
                }
              >
                View
              </Button>
            </Slot>
          ),
        };
      });
  };

  // Prepare data for rendering
  const rowData = quotesData?.items
    ? prepareRowData(quotesData.items, slots, onViewQuote)
    : [];

  const paginationInfo = quotesData?.paginationInfo;
  const shouldShowPagination = !!paginationInfo;

  // Empty state message
  const emptyStateMessage = (
    <Slot name="EmptyQuotes" slot={slots?.EmptyQuotes} context={{ quotesData }}>
      <IllustratedMessage heading="No Quotes Found" />
    </Slot>
  );

  // Item range message
  const itemRangeMessage = paginationInfo ? (
    <Slot name="ItemRange" slot={slots?.ItemRange} context={paginationInfo}>
      <span>
        Items {paginationInfo.startItem} to {paginationInfo.endItem} of{' '}
        {paginationInfo.totalCount} total
      </span>
    </Slot>
  ) : undefined;

  // Prepare page size picker message for component
  const pageSizePickerMessage =
    paginationInfo && paginationInfo.pageSizeOptions ? (
      <Slot
        name="PageSizePicker"
        slot={slots?.PageSizePicker}
        context={{
          pageSize: paginationInfo.pageSize,
          pageSizeOptions: paginationInfo.pageSizeOptions,
          onPageSizeChange: handlePageSizeChange,
        }}
      >
        <span>Show </span>
        <Picker
          variant="primary"
          size="medium"
          value={String(paginationInfo.pageSize)}
          options={paginationInfo.pageSizeOptions.map(
            (size): PickerOption => ({
              value: String(size),
              text: String(size),
            })
          )}
          handleSelect={handlePageSizeSelect}
        />
        <span> per page</span>
      </Slot>
    ) : undefined;

  // Prepare pagination message for component
  const paginationMessage = paginationInfo ? (
    <Slot
      name="Pagination"
      slot={slots?.Pagination}
      context={{
        currentPage: paginationInfo.currentPage,
        totalPages: paginationInfo.totalPages,
        onChange: handlePageChange,
      }}
    >
      <Pagination
        currentPage={paginationInfo.currentPage}
        totalPages={paginationInfo.totalPages}
        onChange={handlePageChange}
      />
    </Slot>
  ) : undefined;

  return (
    <QuotesListTableComponent
      rowData={rowData}
      loading={loading}
      className={props.className as string}
      emptyStateMessage={emptyStateMessage}
      showItemRange={showItemRange && shouldShowPagination}
      itemRangeMessage={itemRangeMessage}
      showPageSizePicker={showPageSizePicker && shouldShowPagination}
      pageSizePickerMessage={pageSizePickerMessage}
      showPagination={showPagination && shouldShowPagination}
      paginationMessage={paginationMessage}
    />
  );
};
